import React, {
  ComponentProps,
  Fragment,
  HTMLAttributes,
  memo,
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Layer,
  Rect,
  Stage,
  Text as KonvaText,
  Image as KonvaImage,
} from 'react-konva';
import useElementSize from '@resources/js/hooks/useElementSize';
import getColorForAbilityType from '@resources/js/helpers/getColorForAbilityType';
import useImage from 'use-image';
import cx from 'classnames';
import Button from '@resources/js/components/Button/Button';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import { KonvaEventObject } from 'konva/lib/Node';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import AbilityIcon, {
  AbilityIconProps,
} from '@resources/js/components/AbilityIcon/AbilityIcon';
import millisecondsToMinutesAndSeconds from '@resources/js/helpers/millisecondsToMinutesAndSeconds';
import { Portal } from 'react-konva-utils';
import { styles } from '@resources/js/helpers/styles';

export type EventTimelineGridViewModel = {
  headerRow: EventTimelineRowViewModel | null;
  rows: EventTimelineRowViewModel[];
  categories: EventTimelineCategoryViewModel[];
  defaultZoom: number;
  normalization: {
    isEnabled: boolean;
    default: RotationSelectedOccurrence | null;
  };
  limit?: number;
};

type EventTimelineCategoryViewModel = {
  id: string;
  label: string;
  abilities: EventTimelineCategoryAbilityViewModel[];
};

type EventTimelineCategoryAbilityViewModel = {
  name: string;
  ids: number[];
  iconUrl: string;
  isSelected: boolean;
};

type SetCategoryAbilityIsSelected = (
  categoryId: string,
  abilityNam: string,
  isSelected: boolean
) => void;

type EventTimelineRowViewModel = {
  label: string;
  iconUrl: string;
  duration: number;
  events: EventTimelineEventViewModel[];
  headerRowOverride: EventTimelineRowViewModel | null;
  labelUrl?: string | null;
  offsetMs?: number | null;
};

type EventTimelineEventViewModel = {
  id: string;
  ability: {
    id: number;
    name: string;
    iconUrl: string;
    type: string;
  };
  timestamp: number;
  duration: number;
  effectDuration: number;
  occurrence: number;
  label: string | null;
  tooltip: string | null;
};

type RotationSelectedOccurrence = {
  abilityId: number;
  abilityName: string;
  occurrence: number;
};

const rowHeight = 24;
const rowGap = styles.tinyGap;
const tickHeight = 24;
const smallTickHeight = 6;
const defaultNormalization: EventTimelineGridViewModel['normalization'] = {
  isEnabled: false,
  default: null,
};

export default function EventTimelineGrid({
  headerRow: initialHeaderRow,
  rows: initialRows,
  categories: initialCategories,
  limit: initialLimit = undefined,
  defaultZoom = 1,
  normalization = defaultNormalization,
}: EventTimelineGridViewModel): JSX.Element {
  const [categories, setCategories] =
    useState<EventTimelineCategoryViewModel[]>(initialCategories);
  const setCategoryAbilityIsSelected = useCallback(
    (categoryId: string, abilityName: string, isSelected: boolean) => {
      setCategories((prevCategories) =>
        prevCategories.map((category) =>
          category.id === categoryId
            ? {
                ...category,
                abilities: category.abilities.map((ability) =>
                  ability.name === abilityName
                    ? { ...ability, isSelected }
                    : ability
                ),
              }
            : category
        )
      );
    },
    []
  );
  const selectedAbilityIds = useMemo(
    () =>
      new Set(
        categories.flatMap((category) =>
          category.abilities
            .filter((ability) => ability.isSelected)
            .flatMap((ability) => ability.ids)
        )
      ),
    [categories]
  );

  const [selectedOccurrence, setSelectedOccurrence] =
    useState<RotationSelectedOccurrence | null>(normalization?.default ?? null);
  const [showMore, setShowMore] = useState(false);
  const limit = initialLimit
    ? showMore
      ? undefined
      : initialLimit
    : undefined;
  const [rows, maxOffsetMs] = useMemo(() => {
    let rows = limit ? initialRows.slice(0, limit) : initialRows;

    rows = rows.map((row) => {
      if (!selectedOccurrence) {
        return {
          ...row,
          offsetMs: 0,
        };
      }

      const selectedOccurrenceEvent = row.events.find(
        (event) =>
          event.ability.id === selectedOccurrence.abilityId &&
          event.occurrence === selectedOccurrence.occurrence
      );

      return {
        ...row,
        offsetMs: selectedOccurrenceEvent
          ? -selectedOccurrenceEvent.timestamp
          : null,
      };
    });

    const minOffsetMs = Math.min(
      ...rows.map((row) => row.offsetMs ?? 1_000_000)
    );

    rows.forEach((row) => {
      if (row.offsetMs !== null && row.offsetMs !== undefined) {
        row.offsetMs -= minOffsetMs;
      }
      if (row.headerRowOverride) {
        row.headerRowOverride.offsetMs = row.offsetMs;
      }
    });

    const maxOffsetMs = Math.max(...rows.map((row) => row.offsetMs ?? 0));

    rows = limit ? rows.slice(0, limit) : rows;

    return [rows, maxOffsetMs];
  }, [initialRows, limit, selectedOccurrence]);
  const headerRow = useMemo(() => {
    if (!initialHeaderRow) {
      return null;
    }

    return {
      ...initialHeaderRow,
      offsetMs: rows[0].offsetMs,
    };
  }, [initialHeaderRow, rows]);

  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [tooltipText, setTooltipText] = useState('');
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const showTooltip = useCallback(
    (e: KonvaEventObject<MouseEvent>, text: string) => {
      const stage = e.target.getStage();
      if (!stage) {
        return;
      }
      const pos = stage.getPointerPosition();
      if (!pos) {
        return;
      }
      setTooltipPos({
        x: pos.x + 5,
        y: pos.y + 5,
      });
      setTooltipText(text);
      setTooltipVisible(true);
    },
    []
  );
  const hideTooltip = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  const [zoom, setZoom] = useState(defaultZoom);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { width: containerWidth } = useElementSize(containerRef);
  const width = containerWidth * zoom;
  const height =
    tickHeight +
    (rows.length + (headerRow ? 1 : 0)) * rowHeight +
    rows.length * rowGap;
  const duration = [headerRow, ...rows]
    .filter(Boolean)
    .map((x) => x?.duration ?? 0)
    .reduce((a, b) => (a > b ? a : b), 0);
  const msToPx = width / (duration + maxOffsetMs);
  const noOp = () => null;

  const [headerRowOverride, setHeaderRowOverride] =
    useState<EventTimelineRowViewModel>();

  return (
    <VerticalContent className={'event-timeline-grid'} gap={'smaller'}>
      <Categories
        categories={categories}
        setCategoryAbilityIsSelected={setCategoryAbilityIsSelected}
      />
      <div className={'event-timeline-grid__grid'} style={{ height }}>
        <div
          className={'event-timeline-grid__row-labels'}
          style={{ gap: rowGap }}
          onMouseOut={() => setHeaderRowOverride(undefined)}
        >
          <div style={{ height: tickHeight - rowGap }}>
            <ZoomControl zoom={zoom} setZoom={setZoom} />
          </div>
          {headerRow && (
            <RowLabel
              index={0}
              {...headerRow}
              onMouseOver={() => setHeaderRowOverride(undefined)}
            />
          )}
          {rows.map((row, index) => (
            <RowLabel
              index={index + 1}
              key={index}
              {...row}
              onMouseOver={() =>
                setHeaderRowOverride(row.headerRowOverride ?? undefined)
              }
            />
          ))}
        </div>
        <div ref={containerRef} className={'event-timeline-grid__canvas'}>
          <Stage width={width} height={height}>
            <Layer name={'row-backgrounds'} />
            <Layer name={'event-backgrounds'} />
            <Layer>
              {selectedOccurrence ? null : (
                <Ticks
                  msToPx={msToPx}
                  duration={duration}
                  tickHeight={tickHeight}
                />
              )}
              {headerRow && (
                <Row
                  {...(headerRowOverride ?? headerRow)}
                  isHeader={true}
                  index={0}
                  msToPx={msToPx}
                  selectedAbilityIds={selectedAbilityIds}
                  selectedOccurrence={selectedOccurrence}
                  setSelectedOccurrence={noOp}
                  showTooltip={showTooltip}
                  hideTooltip={hideTooltip}
                  yOffset={tickHeight}
                />
              )}
              {rows.map((row, index) => (
                <Row
                  key={index}
                  {...row}
                  isHeader={false}
                  index={index + (headerRow ? 1 : 0)}
                  selectedAbilityIds={selectedAbilityIds}
                  selectedOccurrence={selectedOccurrence}
                  setSelectedOccurrence={
                    normalization.isEnabled ? setSelectedOccurrence : noOp
                  }
                  msToPx={msToPx}
                  showTooltip={showTooltip}
                  hideTooltip={hideTooltip}
                  yOffset={tickHeight}
                />
              ))}
            </Layer>
            <Layer name={'top'} />
          </Stage>
          <div className={'event-timeline-grid__canvas-overlay'}>
            {isTooltipVisible && (
              <div
                className={'event-timeline-grid__tooltip'}
                style={getTooltipPositionStyle(
                  tooltipPos.x,
                  tooltipPos.y,
                  width,
                  height
                )}
              >
                <EnhancedMarkdown markdown={tooltipText} wrapped={false} />
              </div>
            )}
          </div>
        </div>
        {selectedOccurrence && (
          <div
            className={'event-timeline-grid__normalization'}
            style={{ height: rowHeight }}
          >
            {trans('reports.normalized_to_cast_x_of_ability', {
              occurrence: selectedOccurrence.occurrence.toString(),
              abilityName: selectedOccurrence.abilityName,
            })}
          </div>
        )}
      </div>
      {initialLimit && initialLimit < initialRows.length && (
        <div>
          <Button onClick={() => setShowMore((prev) => !prev)}>
            {trans(showMore ? 'global.show-less' : 'global.show-more')}
          </Button>
        </div>
      )}
    </VerticalContent>
  );
}

type ZoomControlProps = { zoom: number; setZoom: (zoom: number) => void };

const zoomLevels = [1, 1.5, 2, 3, 4, 5, 6, 7, 8];

function ZoomControl({ zoom, setZoom }: ZoomControlProps): JSX.Element {
  const decreaseZoom = useCallback(() => {
    const nextZoom = zoomLevels
      .slice()
      .reverse()
      .find((level) => level < zoom);
    if (nextZoom !== undefined) {
      setZoom(nextZoom);
    }
  }, [setZoom, zoom]);

  const increaseZoom = useCallback(() => {
    const nextZoom = zoomLevels.find((level) => level > zoom);
    if (nextZoom !== undefined) {
      setZoom(nextZoom);
    }
  }, [setZoom, zoom]);

  const canDecreaseZoom = useMemo(() => zoomLevels.indexOf(zoom) > 0, [zoom]);

  const canIncreaseZoom = useMemo(
    () => zoomLevels.indexOf(zoom) < zoomLevels.length - 1,
    [zoom]
  );

  return (
    <div className={'event-timeline-grid__zoom-control'}>
      <Button
        styles={['primary', 'slim']}
        onClick={decreaseZoom}
        disabled={!canDecreaseZoom}
      >
        -
      </Button>
      <span>{zoom}x</span>
      <Button
        styles={['primary', 'slim']}
        onClick={increaseZoom}
        disabled={!canIncreaseZoom}
      >
        +
      </Button>
    </div>
  );
}

type CategoriesProps = Pick<EventTimelineGridViewModel, 'categories'> & {
  setCategoryAbilityIsSelected: SetCategoryAbilityIsSelected;
};

function Categories({
  categories,
  setCategoryAbilityIsSelected,
}: CategoriesProps): JSX.Element {
  return (
    <div className={'event-timeline-grid__categories'}>
      {categories.map((category) => (
        <Category
          key={category.id}
          {...category}
          setCategoryAbilityIsSelected={setCategoryAbilityIsSelected}
        />
      ))}
    </div>
  );
}

type CategoryProps = EventTimelineCategoryViewModel &
  Pick<CategoriesProps, 'setCategoryAbilityIsSelected'>;

function Category({
  id,
  label,
  abilities,
  setCategoryAbilityIsSelected,
}: CategoryProps): JSX.Element {
  return (
    <div className={'event-timeline-grid__category'}>
      <span className={'event-timeline-grid__category-label'}>{label}</span>
      <span className={'event-timeline-grid__category-label-width-only'}>
        {label}
      </span>
      <div className={'event-timeline-grid__category-abilities'}>
        {abilities.map((ability) => (
          <CategoryAbility
            key={ability.name}
            {...ability}
            categoryId={id}
            setCategoryAbilityIsSelected={setCategoryAbilityIsSelected}
          />
        ))}
      </div>
    </div>
  );
}

type CategoryAbilityProps = EventTimelineCategoryAbilityViewModel & {
  categoryId: string;
} & Pick<CategoriesProps, 'setCategoryAbilityIsSelected'>;

function CategoryAbility({
  categoryId,
  name,
  ids,
  iconUrl,
  isSelected,
  setCategoryAbilityIsSelected,
}: CategoryAbilityProps): JSX.Element {
  const onClick: AbilityIconProps['onClick'] = useCallback(
    (e) => {
      e.preventDefault();
      setCategoryAbilityIsSelected(categoryId, name, !isSelected);
    },
    [categoryId, name, isSelected, setCategoryAbilityIsSelected]
  );

  return (
    <div className={'event-timeline-grid__category-ability'}>
      <AbilityIcon
        id={ids[0]}
        icon={iconUrl}
        rounded={false}
        bordered={true}
        onClick={onClick}
      />
      {!isSelected && (
        <div className={'event-timeline-grid__category-ability-overlay'} />
      )}
    </div>
  );
}

type RowLabelProps = {
  index: number;
  label: string;
  labelUrl?: string | null;
  iconUrl: string;
  onMouseOver: () => void;
};

function RowLabel({
  index,
  label,
  iconUrl,
  onMouseOver,
  labelUrl,
}: RowLabelProps): JSX.Element {
  const Container = labelUrl
    ? (props: HTMLAttributes<HTMLAnchorElement>) => (
        <a {...props} href={labelUrl} target={'_blank'} rel='noreferrer' />
      )
    : (props: HTMLAttributes<HTMLDivElement>) => <div {...props} />;

  return (
    <Container
      className={cx({
        'event-timeline-grid__row-label': true,
        'event-timeline-grid__row-label--header-row': index === 0,
      })}
      style={{ height: rowHeight, gridTemplateColumns: `${rowHeight}px 1fr` }}
      onMouseOver={onMouseOver}
    >
      <div className={'event-timeline-grid__row-label-icon'}>
        <img
          src={iconUrl}
          alt={''}
          style={{ width: rowHeight, height: rowHeight }}
        />
        {index > 0 && (
          <div className={'event-timeline-grid__row-label-icon-overlay'} />
        )}
        {index > 0 && <span>{index}</span>}
      </div>
      <span className={'event-timeline-grid__row-label-label'}>{label}</span>
    </Container>
  );
}

type RowProps = EventTimelineRowViewModel & {
  isHeader: boolean;
  index: number;
  msToPx: number;
  selectedAbilityIds: Set<number>;
  selectedOccurrence: RotationSelectedOccurrence | null;
  setSelectedOccurrence: (
    occurrence: RotationSelectedOccurrence | null
  ) => void;
  showTooltip: (e: KonvaEventObject<MouseEvent>, text: string) => void;
  hideTooltip: () => void;
  yOffset?: number;
};

function Row({
  duration,
  events: initialEvents,
  isHeader,
  index,
  msToPx,
  offsetMs,
  selectedAbilityIds,
  selectedOccurrence,
  setSelectedOccurrence,
  showTooltip,
  hideTooltip,
  yOffset = 0,
}: RowProps): JSX.Element {
  const x = (offsetMs ?? 0) * msToPx;
  const y = yOffset + index * rowHeight + index * rowGap;
  const backgroundWidth = duration * msToPx;

  const clusters = useMemo(() => {
    const clusters: EventTimelineEventViewModel[][] = [];
    let cluster: EventTimelineEventViewModel[] = [];
    let x = -1_000_000;

    initialEvents.forEach((event) => {
      if (!selectedAbilityIds.has(event.ability.id)) {
        return;
      }

      const eventX = msToPx * event.timestamp;

      if (!cluster.length) {
        // Cluster has been reset, so start a new cluster
        cluster.push(event);
      } else {
        if (eventX <= x) {
          // Overlapping, so extend the existing cluster
          cluster.push(event);
        } else {
          // No longer overlapping, so cluster is finished
          if (cluster.length > 1) {
            // Cluster more than a single event, so track it
            clusters.push(cluster);
          }
          // Start new cluster
          cluster = [event];
        }
      }

      // Events are overlapping if their icon is more than 75% covered
      x = eventX + rowHeight * 0.75;
    });
    // Check the final cluster
    if (cluster.length > 1) {
      clusters.push(cluster);
    }

    return clusters;
  }, [initialEvents, msToPx, selectedAbilityIds]);

  const events = useMemo(() => {
    return initialEvents.map((event) => {
      return {
        ...event,
        cluster: clusters.find((cluster) =>
          cluster.some((e) => e.id === event.id)
        ),
      };
    });
  }, [clusters, initialEvents]);

  const reversedEvents = useMemo(() => events.slice().reverse(), [events]);

  const [hoveredClusterIndex, setHoveredClusterIndex] = useState<number>();
  const resetHoveredClusterIndexTimeout = useRef<NodeJS.Timeout>();

  const selectedOccurrenceNotPresent = selectedOccurrence && offsetMs === null;

  return (
    <>
      <Portal selector={'.row-backgrounds'}>
        <Rect
          x={x}
          y={y}
          width={backgroundWidth}
          height={rowHeight}
          fill={isHeader ? '#B4BDFF44' : 'rgb(0, 0, 0, 0.4)'}
        />
      </Portal>
      {/* Header row (index 0) typically shows encounter casts, which often overlap in weird ways, so we show in a different order */}
      {(index === 0 ? events : reversedEvents).map((event) => {
        const clusterIsSelected =
          event.cluster &&
          hoveredClusterIndex !== undefined &&
          clusters[hoveredClusterIndex] === event.cluster;
        const firstEventInCluster = event.cluster?.[0];

        let x = msToPx * event.timestamp;
        if (clusterIsSelected && firstEventInCluster) {
          const indexInCluster =
            event.cluster?.findIndex((e) => e.id === event.id) ?? undefined;
          if (indexInCluster !== undefined) {
            x =
              msToPx * firstEventInCluster.timestamp +
              indexInCluster * rowHeight;
          }
        }
        if (offsetMs) {
          x += msToPx * offsetMs;
        }

        return (
          <Event
            key={event.id}
            {...event}
            isSelected={selectedAbilityIds.has(event.ability.id)}
            isOccurrenceSelected={Boolean(
              selectedOccurrence &&
                selectedOccurrence.abilityId === event.ability.id &&
                selectedOccurrence.occurrence === event.occurrence
            )}
            setSelectedOccurrence={setSelectedOccurrence}
            cornerLabel={
              !clusterIsSelected && firstEventInCluster?.id === event.id
                ? event.cluster?.length.toString() ?? null
                : null
            }
            cluster={event.cluster}
            clusterIndex={clusters.indexOf(event.cluster ?? [])}
            showTooltip={showTooltip}
            hideTooltip={hideTooltip}
            resetHoveredClusterIndexTimeout={resetHoveredClusterIndexTimeout}
            setHoveredClusterIndex={setHoveredClusterIndex}
            x={x}
            y={y}
            msToPx={msToPx}
          />
        );
      })}
      {selectedOccurrenceNotPresent && (
        <Rect
          x={x}
          y={y}
          width={backgroundWidth}
          height={rowHeight}
          fill={'rgb(0, 0, 0, 0.7)'}
        />
      )}
      {!selectedOccurrence && (
        <Text
          x={x + backgroundWidth + styles.smallerGap}
          y={y + 1}
          height={rowHeight}
          text={millisecondsToMinutesAndSeconds(duration)}
          fontSize={13}
          verticalAlign={'middle'}
          fill={styles.white700}
        />
      )}
    </>
  );
}

type EventProps = Omit<EventTimelineEventViewModel, 'timestamp'> & {
  isSelected: boolean;
  isOccurrenceSelected: boolean;
  setSelectedOccurrence: (
    occurrence: RotationSelectedOccurrence | null
  ) => void;
  cornerLabel: string | null;
  cluster?: EventTimelineEventViewModel[];
  showTooltip: (e: KonvaEventObject<MouseEvent>, text: string) => void;
  hideTooltip: () => void;
  clusterIndex: number;
  resetHoveredClusterIndexTimeout: MutableRefObject<NodeJS.Timeout | undefined>;
  setHoveredClusterIndex: (index: number | undefined) => void;
  x: number;
  y: number;
  msToPx: number;
};

const Event = memo(function Event({
  ability,
  duration,
  effectDuration,
  occurrence,
  label,
  tooltip,
  isSelected,
  isOccurrenceSelected,
  setSelectedOccurrence,
  cornerLabel,
  cluster,
  clusterIndex,
  showTooltip,
  hideTooltip,
  x,
  y,
  msToPx,
  resetHoveredClusterIndexTimeout,
  setHoveredClusterIndex,
}: EventProps): JSX.Element | null {
  const width = Math.max(rowHeight, msToPx * Math.max(0, duration - 100));
  const effectWidth = effectDuration
    ? Math.max(rowHeight, msToPx * Math.max(0, effectDuration - 100))
    : null;

  const onMouseMove = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (cluster) {
        if (resetHoveredClusterIndexTimeout.current) {
          clearTimeout(resetHoveredClusterIndexTimeout.current);
        }
        setHoveredClusterIndex(clusterIndex);
      }
      tooltip && showTooltip(e, tooltip);
    },
    [
      cluster,
      clusterIndex,
      resetHoveredClusterIndexTimeout,
      setHoveredClusterIndex,
      showTooltip,
      tooltip,
    ]
  );
  const onMouseOut = useCallback(() => {
    if (resetHoveredClusterIndexTimeout.current) {
      clearTimeout(resetHoveredClusterIndexTimeout.current);
    }
    resetHoveredClusterIndexTimeout.current = setTimeout(
      () => setHoveredClusterIndex(undefined),
      50
    );
    hideTooltip();
  }, [hideTooltip, resetHoveredClusterIndexTimeout, setHoveredClusterIndex]);
  const onClick = useCallback(() => {
    setSelectedOccurrence(
      isOccurrenceSelected
        ? null
        : { abilityId: ability.id, abilityName: ability.name, occurrence }
    );
  }, [
    ability.id,
    ability.name,
    isOccurrenceSelected,
    occurrence,
    setSelectedOccurrence,
  ]);

  if (!isSelected) {
    return null;
  }

  return (
    <>
      <Portal selector={'.event-backgrounds'}>
        {effectWidth && (
          <Rect
            x={x}
            y={y}
            width={effectWidth}
            height={rowHeight}
            fill={getColorForAbilityType(ability.type)}
            opacity={0.3}
          />
        )}
        <Rect
          x={x}
          y={y}
          width={width}
          height={rowHeight}
          fill={getColorForAbilityType(ability.type)}
          opacity={0.5}
        />
      </Portal>
      <URLImage
        src={ability.iconUrl}
        x={x + rowGap / 2}
        y={y + rowGap / 2}
        width={rowHeight - rowGap}
        height={rowHeight - rowGap}
      />
      {label && (
        <Text
          text={label}
          fontStyle={'bold'}
          x={x}
          y={y + rowGap / 2 + 4}
          width={rowHeight}
          align={'center'}
          fill={styles.white900}
        />
      )}
      {cornerLabel && (
        <Text
          text={cornerLabel}
          fontStyle={'bold'}
          x={x + rowHeight * 0.5}
          y={y + rowHeight * 0.5 + 2}
          width={rowHeight * 0.5}
          align={'center'}
          fill={styles.white900}
          fontSize={10}
        />
      )}
      <Rect
        x={x}
        y={y}
        width={width}
        height={rowHeight}
        stroke={styles.level1Border}
        strokeWidth={1}
        onMouseMove={onMouseMove}
        onMouseOut={onMouseOut}
        onClick={onClick}
      />
      {isOccurrenceSelected && (
        <Rect
          x={x}
          y={y}
          width={width}
          height={rowHeight}
          stroke={'yellow'}
          strokeWidth={2}
          isInteractable={false}
          onMouseMove={onMouseMove}
          onMouseOut={onMouseOut}
          onClick={onClick}
        />
      )}
    </>
  );
});

const Text = memo(function Text(
  props: ComponentProps<typeof KonvaText>
): JSX.Element {
  return (
    <KonvaText
      fontSize={17}
      fontFamily={'Segoe UI, Arial, Avenir, sans-serif'}
      shadowOffsetX={1}
      shadowOffsetY={2}
      shadowOpacity={0.85}
      wrap={'none'}
      {...props}
    />
  );
});

const URLImage = memo(function URLImage({
  src,
  ...rest
}: Omit<ComponentProps<typeof KonvaImage>, 'image'> & { src: string }) {
  const [image] = useImage(src);
  return <KonvaImage image={image} {...rest} />;
});

function getTooltipPositionStyle(
  x: number,
  y: number,
  canvasWidth: number,
  canvasHeight: number
) {
  const tooCloseToRight = x + 200 > canvasWidth;
  const tooCloseToBottom = y + 50 > canvasHeight;

  return {
    left: tooCloseToRight ? x - 200 : x + 10,
    top: tooCloseToBottom ? y - 50 : y,
  };
}

const possibleTickIntervals = [
  { maximumMsToPx: 1, tickInterval: 1, smallTickInterval: 0.2 },
  { maximumMsToPx: 0.12, tickInterval: 5, smallTickInterval: 1 },
  { maximumMsToPx: 0.015, tickInterval: 10, smallTickInterval: 2 },
  { maximumMsToPx: 0.01, tickInterval: 15, smallTickInterval: 3 },
  { maximumMsToPx: 0.005, tickInterval: 30, smallTickInterval: 10 },
  { maximumMsToPx: 0.0025, tickInterval: 60, smallTickInterval: 15 },
];

const Ticks = memo(function Ticks({
  msToPx,
  duration,
  tickHeight,
}: {
  msToPx: number;
  duration: number;
  tickHeight: number;
}) {
  const ticks = useMemo(() => {
    const durationSeconds = Math.ceil(duration / 1000);
    const tickIntervals =
      possibleTickIntervals.findLast((x) => msToPx < x.maximumMsToPx) ??
      possibleTickIntervals[0];
    const tickIntervalSeconds = tickIntervals.tickInterval;
    const smallTickIntervalSeconds = tickIntervals.smallTickInterval;

    // Large ticks with labels
    const largeTickSeconds = Array.from(
      { length: Math.floor(durationSeconds / tickIntervalSeconds) + 1 },
      (_, i) => i * tickIntervalSeconds
    );
    const largeTicks = largeTickSeconds.map((s) => {
      const xPos = msToPx * s * 1000;
      const minutes = Math.floor(s / 60)
        .toString()
        .padStart(2, '0');
      const seconds = (s % 60).toString().padStart(2, '0');
      const label = `${minutes}:${seconds}`;
      return (
        <Fragment key={`tick-${s}`}>
          <Rect
            x={xPos}
            y={0}
            width={1}
            height={tickHeight}
            fill={styles.white700}
          />
          <Text
            text={label}
            x={xPos + styles.smallerGap}
            y={3}
            fill={styles.white700}
            fontSize={13}
          />
        </Fragment>
      );
    });

    // Small ticks (no label)
    const smallTickSeconds = Array.from(
      { length: Math.floor(durationSeconds / smallTickIntervalSeconds) + 1 },
      (_, i) => i * smallTickIntervalSeconds
    ).filter((s) => s % tickIntervalSeconds !== 0);

    const smallTicks = smallTickSeconds.map((s) => {
      const xPos = msToPx * s * 1000;
      return (
        <Rect
          key={`small-tick-line-${s}`}
          x={xPos}
          y={tickHeight - smallTickHeight}
          width={1}
          height={smallTickHeight}
          fill={styles.white700}
          opacity={0.7}
        />
      );
    });

    return [...largeTicks, ...smallTicks];
  }, [msToPx, duration, tickHeight]);

  return <>{ticks}</>;
});
